import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, File, X, AlertTriangle, CheckCircle, FileText, Car, Stamp, FileSignature, Mail, Download } from 'lucide-react';

/**
 * Document types for vehicle exit workflow
 * Note: required can be true, false, or 'conditional' (depends on reason)
 * Labels/descriptions resolved via t() from vehicles.wizard.exitDocuments.types.*
 */
const EXIT_DOCUMENT_TYPES = [
  { type: 'CERFA_CESSION', icon: Stamp, required: true },
  { type: 'REGISTRATION_CANCELLED', icon: FileSignature, required: true },
  { type: 'LEASE_END_DOCUMENT', icon: FileText, required: 'conditional', condition: 'lease_end' },
  { type: 'SALE_INVOICE', icon: FileText, required: false, condition: 'sale' },
  { type: 'INSURANCE_TERMINATION', icon: FileText, required: false },
  { type: 'VEHICLE_PHOTO_EXIT', icon: Car, required: false },
  { type: 'CLAIM_DOCUMENT', icon: AlertTriangle, required: 'conditional', condition: 'claim' },
];

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.doc', '.docx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

/**
 * StepExitDocuments - Document collection step for vehicle exit workflow
 */
function StepExitDocuments({ formData, updateFormData }) {
  const { t } = useTranslation();
  const docLabel = (type) => t(`vehicles.wizard.exitDocuments.types.${type}.label`);
  const docDescription = (type) => t(`vehicles.wizard.exitDocuments.types.${type}.description`);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Get pending documents from formData
  const pendingDocuments = formData.pendingExitDocuments || [];

  // Filter document types based on exit reason and compute effective required status
  const getRelevantDocTypes = () => {
    return EXIT_DOCUMENT_TYPES
      .filter(docType => {
        if (!docType.condition) return true;
        if (docType.condition === 'lease_end') return formData.reason === 'lease_end';
        if (docType.condition === 'sale') return formData.reason === 'sale';
        if (docType.condition === 'claim') {
          return ['claim_theft', 'claim_accident', 'claim_other'].includes(formData.reason);
        }
        return true;
      })
      .map(docType => ({
        ...docType,
        // Compute effective required status
        isRequired: docType.required === true ||
          (docType.required === 'conditional' && docType.condition === 'lease_end' && formData.reason === 'lease_end') ||
          (docType.required === 'conditional' && docType.condition === 'claim' && ['claim_theft', 'claim_accident', 'claim_other'].includes(formData.reason))
      }));
  };

  const relevantDocTypes = getRelevantDocTypes();

  // Validate file
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: t('vehicles.wizard.exitDocuments.extensionNotAllowed', { ext }) };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t('vehicles.wizard.exitDocuments.fileTooLarge') };
    }
    return { valid: true };
  };

  // Handle file selection
  const handleFiles = useCallback((files) => {
    if (!selectedType) {
      setError(t('vehicles.wizard.exitDocuments.selectTypeFirst'));
      return;
    }
    setError('');

    const newDocs = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        newDocs.push({
          id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file,
          fileName: file.name,
          fileSize: file.size,
          documentType: selectedType,
          documentLabel: EXIT_DOCUMENT_TYPES.some(d => d.type === selectedType) ? docLabel(selectedType) : selectedType,
          status: 'pending'
        });
      } else {
        setError(validation.error);
      }
    }

    if (newDocs.length > 0) {
      updateFormData('pendingExitDocuments', [...pendingDocuments, ...newDocs]);
      setSelectedType('');
    }
  }, [selectedType, pendingDocuments, updateFormData, t]);

  // Remove document from queue
  const removeDocument = (docId) => {
    updateFormData('pendingExitDocuments', pendingDocuments.filter(d => d.id !== docId));
  };

  // Drag handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = '';
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Count documents by type
  const getDocCountForType = (type) => {
    return pendingDocuments.filter(d => d.documentType === type).length;
  };

  // Check required documents (using computed isRequired)
  const requiredDocs = relevantDocTypes.filter(d => d.isRequired);
  const missingRequired = requiredDocs.filter(d => getDocCountForType(d.type) === 0);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.exitDocuments.title')}</h3>

      {/* Required Documents Warning */}
      {missingRequired.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-900">{t('vehicles.wizard.exitDocuments.missingRequiredTitle')}</h4>
              <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                {missingRequired.map(doc => (
                  <li key={doc.type}>{docLabel(doc.type)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">{t('vehicles.wizard.exitDocuments.requiredBannerTitle')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('vehicles.wizard.exitDocuments.requiredBannerHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Document Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('vehicles.wizard.exitDocuments.typeToAddLabel')}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {relevantDocTypes.map(docType => {
            const count = getDocCountForType(docType.type);
            const Icon = docType.icon;
            const isMissing = docType.isRequired && count === 0;
            return (
              <button
                key={docType.type}
                type="button"
                onClick={() => setSelectedType(docType.type)}
                className={`p-3 border-2 rounded-lg text-left transition-all relative ${
                  selectedType === docType.type
                    ? 'border-red-500 bg-red-50'
                    : isMissing
                      ? 'border-red-300 bg-red-50/30 hover:border-red-400'
                      : count > 0
                        ? 'border-green-300 bg-green-50/30 hover:border-green-400'
                        : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                }`}
              >
                {/* Required badge */}
                {docType.isRequired && (
                  <div className={`absolute -top-2 -right-2 text-xs font-bold px-2 py-0.5 rounded-full ${
                    count > 0 ? 'bg-green-500 text-white' : 'bg-red-500 text-white animate-pulse'
                  }`}>
                    {count > 0 ? t('vehicles.wizard.exitDocuments.okBadge') : t('vehicles.wizard.exitDocuments.requiredBadge')}
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Icon className={`w-5 h-5 flex-shrink-0 ${
                    selectedType === docType.type ? 'text-red-600' :
                    isMissing ? 'text-red-500' :
                    count > 0 ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium text-sm truncate ${
                        isMissing ? 'text-red-900' : 'text-gray-900'
                      }`}>
                        {docLabel(docType.type)}
                      </span>
                      {count > 0 && (
                        <span className="bg-green-100 text-green-700 text-xs px-1.5 rounded-full">
                          {count}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs truncate ${isMissing ? 'text-red-600' : 'text-gray-500'}`}>
                      {docDescription(docType.type)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Drop Zone */}
      {selectedType && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 hover:border-red-400 hover:bg-gray-50'
            }
          `}
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-red-500' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-600 mb-1">
            {t('vehicles.wizard.exitDocuments.dropHint', { type: docLabel(selectedType) })}
          </p>
          <p className="text-xs text-gray-400">
            {t('vehicles.wizard.exitDocuments.browseHint')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Queued Documents */}
      {pendingDocuments.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="font-medium text-gray-900 text-sm">
              {t('vehicles.wizard.exitDocuments.queuedTitle', { count: pendingDocuments.length })}
            </h4>
          </div>
          <div className="divide-y">
            {pendingDocuments.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                <File className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                      {doc.documentLabel}
                    </span>
                    <span>{formatSize(doc.fileSize)}</span>
                  </div>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <button
                  type="button"
                  onClick={() => removeDocument(doc.id)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Documents Message */}
      {pendingDocuments.length === 0 && !selectedType && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{t('vehicles.wizard.exitDocuments.noDocuments')}</p>
          <p className="text-xs mt-1">{t('vehicles.wizard.exitDocuments.selectTypeHint')}</p>
        </div>
      )}
    </div>
  );
}

export default StepExitDocuments;
export { EXIT_DOCUMENT_TYPES };
